import { ItemsPerPage, useItemsPerPage } from '@billy/design-system'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../../../config/queryClient'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { Scope } from '../../../../../enums/scope'
import { usePaginationPrefetching } from '../../../../../hooks/usePaginationPrefetching'
import { Paging } from '../../../../../types/metable'
import { isAuthorized } from '../../../../../utils/isAuthorized'
import { fetchVatSuggestions } from '../query-api'

interface UseFetchSuggestionsProps {
  entryDatePeriod?: string
}

interface UseFetchSuggestionsResponse {
  isLoading: boolean
  isError?: boolean
  suggestions: VatSuggestion[]
  page: number
  pageSize: ItemsPerPage
  pagination?: Paging
  setPage: (page: number) => void
  setPageSize: (pageSize: ItemsPerPage) => void
  totalSuggestions?: number
}

export interface VatSuggestion {
  amount: number
  confidence: number
  attachmentId: string
  currentAccountId?: string
  currentTaxRateId: string
  entryDate: string
  id: string
  isRejected: boolean
  isApproved: boolean
  organizationId: string
  subjectDescription: string
  suggestedTaxRateId: string
  suggestionDescription: string
}

export interface FetchVatSuggestionsResponseData {
  vatSuggestions: VatSuggestion[]
  meta: {
    paging: Paging
    statusCode: number
    success: boolean
    time: number
  }
}

interface QueryProps {
  organizationId: string
  entryDatePeriod?: string
  page?: number
  pageSize?: ItemsPerPage
}

export const useFetchSuggestions = ({ entryDatePeriod }: UseFetchSuggestionsProps): UseFetchSuggestionsResponse => {
  const { organization } = useUserOrganization()
  const previousPagination = useRef<Paging | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useItemsPerPage()
  const hasUpsellScope = isAuthorized(Scope.VatHelperUpsell)
  const hasWriteScope = isAuthorized(Scope.VatHelperWrite)
  const hasTaxHelperAccess = hasUpsellScope || hasWriteScope

  const organizationId = organization?.id || ''

  const queryProps: QueryProps = useMemo(
    () => ({
      organizationId,
      ...(entryDatePeriod ? { entryDatePeriod } : {}),
      ...(page ? { page } : {}),
      ...(pageSize ? { pageSize } : {}),
    }),
    [organizationId, entryDatePeriod, page, pageSize],
  )

  const queryKey = useMemo((): [QueryKeys, QueryProps] => [QueryKeys.VatSuggestions, queryProps], [queryProps])

  const { data, isLoading, isError, isIdle } = useQuery<FetchVatSuggestionsResponseData>(
    queryKey,
    () =>
      fetchVatSuggestions({
        organizationId: queryProps.organizationId,
        payload: {
          ...(queryProps.entryDatePeriod ? { entryDatePeriod: queryProps.entryDatePeriod } : {}),
          ...(queryProps.page ? { page: queryProps.page } : {}),
          ...(queryProps.pageSize ? { pageSize: queryProps.pageSize } : {}),
        },
      }),
    {
      enabled: !!organizationId && !!entryDatePeriod && hasTaxHelperAccess,
      staleTime: defaultStaleTime,
    },
  )

  useEffect(() => {
    if (data?.meta.paging) {
      previousPagination.current = { ...data.meta.paging }
    }
  }, [data?.meta.paging])

  const getPrefetchQueryKey = useMemo(
    () =>
      (props: QueryProps): [QueryKeys, QueryProps] => [QueryKeys.VatSuggestions, props],
    [],
  )

  const getPrefetchQueryFunction = useMemo(
    () => (props: QueryProps) =>
      fetchVatSuggestions({
        organizationId: props.organizationId,
        payload: {
          ...(props.entryDatePeriod ? { entryDatePeriod: props.entryDatePeriod } : {}),
          ...(props.page ? { page: props.page } : {}),
          ...(props.pageSize ? { pageSize: props.pageSize } : {}),
        },
      }),
    [],
  )

  usePaginationPrefetching<QueryProps, FetchVatSuggestionsResponseData>({
    getQueryFunction: getPrefetchQueryFunction,
    getQueryKey: getPrefetchQueryKey,
    isEnabled: !!organizationId && !!entryDatePeriod && !isLoading,
    queryProps,
    totalPages: data?.meta.paging?.pageCount as number,
  })

  return {
    isError,
    isLoading: isIdle || isLoading,
    suggestions: data?.vatSuggestions || [],
    page,
    pageSize,
    pagination: data?.meta.paging || previousPagination.current,
    setPage,
    setPageSize,
    totalSuggestions: data?.meta.paging?.total,
  }
}
